function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

class Tag {
  constructor(root, options) {
    this.root = root;
    this.wrapper = this.root.querySelector('.js-tags-wrapper');
    this.tagListItems = shuffle(Array.from(this.root.querySelectorAll('.js-tags-tag')));

    this.hasRendered = false;
    this.motionDisabled = false;
    this.options = {
      shape: options?.shape ?? 'ellipse',
      reverse: options?.reverse ?? false,
    };
    this.tagData = [];

    this.init();
  }

  init() {
    // Don't init if there are no tags
    if (!this.tagListItems.length) {
      return;
    }

    this.initElements();
    this.initEvents();
    this.initTagData();

    this.animate();
  }

  get wrapperRect() {
    const tagRects = this.tagListItems.map(tag => tag.getBoundingClientRect());
    const maxTagHeight = Math.max(...tagRects.map(rect => rect.height));
    const maxTagWidth = Math.max(...tagRects.map(rect => rect.width));

    const wrapperRect = this.wrapper.getBoundingClientRect();
    const wrapperWidth = wrapperRect.width - maxTagWidth;
    const wrapperHeight = wrapperRect.height - maxTagHeight;

    return {
      top: maxTagHeight / 2,
      left: maxTagWidth / 2,
      width: wrapperWidth,
      height: wrapperHeight,
    };
  }

  /**
   * Gets the duration of one orbit rotation, given that a orbit
   * perimeter of 750px should last 100,000ms.
   *
   * Equation: (100,000ms / 750px) = (y / perimeter)
   */
  get duration() {
    const rect = this.wrapperRect;
    const minorAxis = Math.min(rect.width, rect.height) / 2;
    const majorAxis = Math.max(rect.width, rect.height) / 2;
    const perimeter = 2 * Math.PI * Math.sqrt((minorAxis * 2 + majorAxis * 2) / 2);
    return perimeter * (100_000 / 750);
  }

  initElements() {
    const wrapperRect = this.wrapperRect;
    this.wrapper.style.setProperty('--top', `${wrapperRect.top}px`);
    this.wrapper.style.setProperty('--left', `${wrapperRect.left}px`);

    if (this.options.shape === 'ellipse') {
      this.circle = document.createElement('div');
      this.circle.classList.add('js-tags-circle');
      this.wrapper.appendChild(this.circle);
    } else if (this.options.shape === 'graph') {
      this.graphSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      this.wrapper.appendChild(this.graphSvg);
      this.graphSvgPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      this.graphSvg.appendChild(this.graphSvgPath);
    }
  }

  initEvents() {
    // Disable motion on user preference
    const mediaQuery = '(prefers-reduced-motion: reduce)';
    const mediaQueryList = window.matchMedia(mediaQuery);
    // Temporarily removing animation. Need for further conversation about tags. - AWH, 7/31/2023
    // this.motionDisabled = mediaQueryList.matches;
    this.motionDisabled = true;
    mediaQueryList.addEventListener('change', evt => {
      this.motionDisabled = evt.matches;
    });

    this.tagListItems.forEach(tagListItem => {
      // Disable animation on hover
      tagListItem.addEventListener('pointerover', () => {
        tagListItem.setAttribute('data-hover', 'true');
      });
      tagListItem.addEventListener('pointerleave', () => {
        tagListItem.removeAttribute('data-hover');
      });

      // Disable animation on focus
      tagListItem.addEventListener('focusin', () => {
        tagListItem.setAttribute('data-focus', 'true');
      });
      tagListItem.addEventListener('focusout', () => {
        tagListItem.removeAttribute('data-focus');
      });
    });

    const observer = new ResizeObserver(() => {
      this.initTagData();
      this.update();
    });
    observer.observe(this.wrapper);
  }

  initTagData() {
    const wrapperRect = this.wrapperRect;

    if (this.options.shape === 'graph') {
      this.graphSvg.setAttribute(
        'viewBox',
        `0 0 ${Math.max(0, wrapperRect.width)} ${Math.max(0, wrapperRect.height)}`,
      );
    }

    // Randomize the angle the tags begin at
    const angleOffset = Math.PI * 2 * Math.random();

    this.tagData = this.tagListItems.map((tagListItem, index) => {
      const tagRect = tagListItem.getBoundingClientRect();

      const x = wrapperRect.width / 2 - tagRect.width / 2 + wrapperRect.left;
      const y = wrapperRect.height / 2 - tagRect.height / 2 + wrapperRect.top;

      const width = tagRect.width;
      const height = tagRect.height;

      let anglePosition;
      if (this.options.shape === 'graph' && this.tagListItems.length === 3) {
        // distribute tags along ellipse to form isosceles triangle
        // • • - • -
        const numPositions = 5;
        const position = [0, 1, 3][index];
        anglePosition = position / numPositions;
      } else {
        // otherwise, evenly distribute
        anglePosition = (index + 1) / this.tagListItems.length;
      }

      let startAngle = Math.PI * 2 * anglePosition + angleOffset;
      let curAngle = startAngle;
      if (this.tagData[index]) {
        startAngle = this.tagData[index]?.startAngle;
        curAngle = this.tagData[index]?.curAngle;
      }

      return { x, y, width, height, startAngle, curAngle };
    });
  }

  animate() {
    if (!this.hasRendered) {
      // Render the tags at least once
      this.update();
      this.wrapper.removeAttribute('aria-hidden');
      this.hasRendered = true;
    } else if (!this.motionDisabled) {
      // Continue moving so long as motion isn't disabled
      this.update();
    }

    // Loop
    requestAnimationFrame(() => this.animate());
  }

  update() {
    const wrapperRect = this.wrapperRect;

    // Keep track of all tag positions (centered around tag)
    const positions = [];

    // convert from ms (1000/s) to frames (60/s)
    const frameDuration = (this.duration / 1000) * 60;
    const direction = this.options.reverse ? 1 : -1;
    const angleChange = (direction * (Math.PI * 2)) / frameDuration;

    this.tagListItems.forEach((tagListItem, index) => {
      if (!tagListItem.dataset.hover && !tagListItem.dataset.focus) {
        this.tagData[index].curAngle += angleChange;
      }

      // https://www.mathopenref.com/coordparamellipse.html
      // Calculate the tag x position
      const a = wrapperRect.width / 2;
      const x = a * Math.cos(this.tagData[index].curAngle) * -1;
      // Calculate the tag y position
      const b = wrapperRect.height / 2;
      const y = b * Math.sin(this.tagData[index].curAngle);

      // Update the tag's translation
      tagListItem.style.transform = `translateX(${this.tagData[index].x + x}px) translateY(${
        this.tagData[index].y + y
      }px)`;

      // And log the position (centered in the tag)
      positions.push({
        x: this.tagData[index].x + x + this.tagData[index].width / 2 - wrapperRect.left,
        y: this.tagData[index].y + y + this.tagData[index].height / 2 - wrapperRect.top,
      });
    });

    if (this.options.shape === 'graph') {
      // Generate the graph path
      const [first, ...rest] = positions;
      // Move point to first position
      let pathD = `M ${first.x} ${first.y} `;
      rest.forEach(position => {
        // Draw lines to subsequent positions
        pathD += `L ${position.x} ${position.y} `;
      });
      // Then close the path
      pathD += 'Z';
      this.graphSvgPath.setAttribute('d', pathD);
    }
  }
}

export default Tag;
